import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Heart, MapPin, SlidersHorizontal, Star, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import Searchbar from "../../Components/Searchbar";
import { University } from "../../types/University";
import UniversityCard from "../../Components/Dashboard/ExplorePage/UniversityCard";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";

const TYPES = ["All", "Local", "Overseas"];

export default function ExplorePage() {
  const [searchParams] = useSearchParams();
  const [universities, setUniversities] = useState<University[]>([]);
  const [savedSlugs, setSavedSlugs] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("All");
  const [type, setType] = useState(searchParams.get("type") || "All");
  const [showSaved, setShowSaved] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);

  const toggleSave = (slug: string) =>
    setSavedSlugs((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });

  const fetchFromFirestore = async (isInitial: boolean) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    if (isInitial) setIsLoading(true);

    try {
      const constraints: any[] = [];

      if (type === "Local") {
        constraints.push(where("country", "==", "Malaysia"));
      } else if (type === "Overseas") {
        constraints.push(where("country", "!=", "Malaysia"));
      } else if (country !== "All") {
        constraints.push(where("country", "==", country));
      }

      const q =
        constraints.length > 0
          ? query(collection(db, "university-listing"), ...constraints)
          : query(collection(db, "university-listing"));

      const snapshot = await getDocs(q);
      let docs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as University[];

      // Sort client-side
      docs.sort((a, b) =>
        (a.universityName || "").localeCompare(b.universityName || "")
      );

      if (search) {
        const searchLower = search.toLowerCase();
        docs = docs.filter(
          (u) =>
            u.universityName?.toLowerCase().includes(searchLower) ||
            u.country?.toLowerCase().includes(searchLower)
        );
      }

      if (isInitial) {
        setUniversities(docs);
      } else {
        setUniversities((prev) => [...prev, ...docs]);
      }

      setTotalCount(docs.length);
      setHasMore(false);
    } catch (err) {
      console.error("Error fetching universities from Firestore:", err);
      if (isInitial) {
        setUniversities([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } finally {
      isFetchingRef.current = false;
      if (isInitial) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFromFirestore(true);
  }, [search, country, type]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 500 &&
        hasMore &&
        !isFetchingRef.current
      ) {
        fetchFromFirestore(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, universities.length]);

  const countries = useMemo(() => {
    return [
      "All",
      ...new Set(universities.map((u) => u.country).filter(Boolean)),
    ];
  }, [universities]);

  const displayed = useMemo(() => {
    if (showSaved) return universities.filter((u) => savedSlugs.has(u.slug));
    return universities;
  }, [universities, showSaved, savedSlugs]);

  const savedCount = savedSlugs.size;
  const hasFilters = country !== "All" || type !== "All" || showSaved;

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-2.5 mb-2">
          <Star className="w-6 h-6 text-orange-500" />
          <h1
            className="text-[28px] text-slate-900 tracking-tight"
            style={{ fontWeight: 900 }}
          >
            Recommended Universities
          </h1>
        </div>
        <p className="text-[14px] text-slate-500" style={{ fontWeight: 400 }}>
          Universities SCS Education can help you compare. Fees and entry
          requirements will be confirmed by a counsellor.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="mb-6"
      >
        <div className="flex gap-3 mb-3">
          <Searchbar
            value={search}
            onChange={setSearch}
            placeholder="Search universities"
          />
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`flex items-center gap-2 px-4 py-3 rounded-2xl border text-[13px] transition-all ${filtersOpen || hasFilters ? "bg-violet-50 border-violet-300 text-violet-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
            style={{ fontWeight: 600 }}
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span className="hidden sm:block">Filters</span>
            {hasFilters && (
              <span className="w-2 h-2 rounded-full bg-violet-600" />
            )}
          </button>
        </div>

        <AnimatePresence>
          {filtersOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 overflow-hidden"
            >
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label
                    className="block text-[11px] text-slate-500 mb-2 uppercase tracking-wider"
                    style={{ fontWeight: 700 }}
                  >
                    Country
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {countries.map((c) => (
                      <button
                        key={c}
                        onClick={() => setCountry(c)}
                        className={`px-3 py-1.5 rounded-full text-[12px] border transition-all ${country === c ? "bg-violet-50 border-violet-300 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                        style={{ fontWeight: country === c ? 600 : 400 }}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label
                    className="block text-[11px] text-slate-500 mb-2 uppercase tracking-wider"
                    style={{ fontWeight: 700 }}
                  >
                    Type
                  </label>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {TYPES.map((t) => (
                      <button
                        key={t}
                        onClick={() => setType(t)}
                        className={`px-3 py-1.5 rounded-full text-[12px] border transition-all ${type === t ? "bg-violet-50 border-violet-300 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                        style={{ fontWeight: type === t ? 600 : 400 }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  {/* <button
                    onClick={() => setShowSaved(!showSaved)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] border transition-all ${showSaved ? "bg-red-50 border-red-200 text-red-600" : "border-slate-200 text-slate-600"}`}
                    style={{ fontWeight: showSaved ? 600 : 400 }}
                  >
                    <Heart
                      className={`w-3 h-3 ${showSaved ? "fill-red-500 text-red-500" : ""}`}
                    />{" "}
                    Saved only ({savedCount})
                  </button> */}
                </div>
              </div>
              {hasFilters && (
                <button
                  onClick={() => {
                    setCountry("All");
                    setType("All");
                    setShowSaved(false);
                  }}
                  className="mt-3 text-[12px] text-red-500 hover:text-red-600 flex items-center gap-1"
                  style={{ fontWeight: 600 }}
                >
                  <X className="w-3 h-3" /> Clear all filters
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-4 mb-5 flex-wrap"
      >
        <p className="text-[13px] text-slate-500" style={{ fontWeight: 500 }}>
          Showing{" "}
          <span className="text-slate-900" style={{ fontWeight: 700 }}>
            {isLoading ? "..." : displayed.length}
          </span>{" "}
          {totalCount !== null && !isLoading && (
            <>
              of{" "}
              <span className="text-slate-900" style={{ fontWeight: 700 }}>
                {totalCount}
              </span>{" "}
            </>
          )}
          universities
        </p>
        <div className="flex items-center gap-2">
          {/* <span
            className="flex items-center gap-1.5 text-[12px] text-slate-500"
            style={{ fontWeight: 500 }}
          >
            <Heart className="w-3.5 h-3.5 fill-red-400 text-red-400" />{" "}
            {savedCount} saved
          </span> */}
          <span
            className="flex items-center gap-1.5 text-[12px] text-slate-500"
            style={{ fontWeight: 500 }}
          >
            <MapPin className="w-3.5 h-3.5 text-violet-400" />{" "}
            {[...new Set(displayed.map((u) => u.country))].length} countries
          </span>
        </div>
      </motion.div>

      {isLoading && (
        <div className="flex items-center justify-center py-20 text-slate-400 text-[14px]">
          Loading universities...
        </div>
      )}

      {!isLoading && (
        <motion.div layout className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          <AnimatePresence>
            {displayed.map((uni, i) => (
              <motion.div
                key={uni.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <UniversityCard
                  uni={uni}
                  saved={savedSlugs.has(uni.slug)}
                  onToggleSave={toggleSave}
                  isRecommended={i < 3}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {!isLoading && displayed.length === 0 && (
        <div className="flex items-center justify-center py-20 text-slate-400 text-[14px]">
          No universities match your search.
        </div>
      )}

      {hasMore && !isLoading && universities.length > 0 && (
        <div className="flex items-center justify-center py-8 text-slate-400 text-[13px]">
          Scroll down to load more...
        </div>
      )}

      {displayed.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-10 bg-slate-900 rounded-3xl p-8 text-center relative overflow-hidden"
        >
          <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-violet-500/15 blur-3xl" />
          <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl" />
          <p
            className="text-[13px] text-slate-400 mb-2 relative z-10"
            style={{ fontWeight: 500 }}
          >
            Can't find what you're looking for?
          </p>
          <h3
            className="text-[22px] text-white mb-4 relative z-10 tracking-tight"
            style={{ fontWeight: 800 }}
          >
            Ask counsellor to find your perfect match
          </h3>
          <a
            href="https://wa.me/601112870256"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-cyan-500 text-white text-[14px] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5 relative z-10"
            style={{ fontWeight: 700 }}
          >
            Chat with Counselor
          </a>
        </motion.div>
      )}
    </div>
  );
}
